import { useState, useEffect } from "react";
import { DateTime } from "luxon";
import {
  SidePanel,
  SidePanelContent,
  SidePanelHeader,
  SidePanelBody,
  SidePanelFooter,
} from "@/components/ui/SidePanel";
import { Button } from "@/components/ui/Button";
import { Icon } from "@/components/ui/Icon";
import { useUpdateUserProfileMutation } from "@/api/clubsApi";
import type { UserProfile } from "@/api/clubsApi";
import { cn } from "@/components/ui/cn";

interface EditSocialModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  profile: UserProfile;
}

const inputClassName = cn(
  "w-full rounded-2xl border border-border-tertiary p-3 pl-10",
  "text-base text-text-primary bg-input-background",
  "placeholder:text-text-tertiary",
  "focus:outline-none focus:ring-2 focus:ring-text-primary focus:ring-offset-2 focus:ring-offset-background",
  "focus:border-text-primary"
);

export function EditSocialModal({
  open,
  onOpenChange,
  profile,
}: EditSocialModalProps) {
  const [updateProfile, { isLoading: isSaving }] = useUpdateUserProfileMutation();

  // Form state
  const [instagram, setInstagram] = useState("");
  const [tiktok, setTiktok] = useState("");
  const [linkedin, setLinkedin] = useState("");
  const [strava, setStrava] = useState("");

  // Initialize form state when modal opens
  useEffect(() => {
    if (open && profile) {
      setInstagram(profile.instagram || "");
      setTiktok(profile.tiktok || "");
      setLinkedin(profile.linkedin || "");
      setStrava(profile.strava || "");
    }
  }, [open, profile]);

  const handleSave = async () => {
    try {
      await updateProfile({
        id: profile.id,
        instagram: instagram.trim() || null,
        tiktok: tiktok.trim() || null,
        linkedin: linkedin.trim() || null,
        strava: strava.trim() || null,
        updated_at: DateTime.now().toISO(),
      }).unwrap();
      onOpenChange(false);
    } catch (error) {
      console.error("Failed to update profile:", error);
    }
  };

  return (
    <SidePanel open={open} onOpenChange={onOpenChange}>
      <SidePanelContent>
        <SidePanelHeader title="Edit social links" onClose={() => onOpenChange(false)} />

        <SidePanelBody className="space-y-4">
          <SocialInput
            icon="instagram"
            value={instagram}
            onChange={setInstagram}
            placeholder="Username"
            label="Instagram"
          />

          <SocialInput
            icon="tiktok"
            value={tiktok}
            onChange={setTiktok}
            placeholder="Username"
            label="TikTok"
          />

          <SocialInput
            icon="linkedin"
            value={linkedin}
            onChange={setLinkedin}
            placeholder="Username"
            label="LinkedIn"
          />

          <SocialInput
            icon="strava"
            value={strava}
            onChange={setStrava}
            placeholder="Athlete ID"
            label="Strava"
          />
        </SidePanelBody>

        <SidePanelFooter className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving}
            className="bg-text-primary text-text-reversed hover:bg-text-primary/90"
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </SidePanelFooter>
      </SidePanelContent>
    </SidePanel>
  );
}

function SocialInput({
  icon,
  value,
  onChange,
  placeholder,
  label,
}: {
  icon: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  label: string;
}) {
  return (
    <div className="space-y-1.5">
      <label className="text-body-3 text-text-secondary">{label}</label>
      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2">
          <Icon name={icon} size={16} className="text-text-secondary" />
        </div>
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={inputClassName}
        />
      </div>
    </div>
  );
}
